import { useMemo } from 'react';
import { ClipboardList } from 'lucide-react';
import type { RequisitionReport, RequisitionStatus } from '@eombor/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { requisitionStatusText } from '../../../shared/i18n/labels.js';
import type { SliceDatum } from '../model/chart-data.js';
import { ReportCard } from './ReportCard.js';
import { StatusDonut } from './StatusDonut.js';

/**
 * Holat ranglari — OQIM bo'ylab pog'ona, tasodifiy emas.
 *
 * Rang talabnomaning oqimdagi joyini bildiradi: qoralama so'nik kulrang,
 * yo'ldagilari kul-ko'kdan to'q navygacha quyuqlashadi, kutayotgani
 * sariq (e'tibor talab qiladi), yakunlangani yashil, rad etilgani qizil.
 *
 * Ro'yxat TO'LIQ (`Record`, `Partial` emas): yangi holat qo'shilsa
 * TypeScript shu yerni yiqitadi. Zaxira rang bo'lsa, yangi holat
 * jimgina boshqasining rangida chizilib, ikkalasi bir xil ko'rinardi.
 */
const STATUS_COLOR: Record<RequisitionStatus, string> = {
  DRAFT: 'var(--color-text-faint)',
  SUBMITTED: 'var(--color-info)',
  ORG_APPROVED: 'var(--color-accent)',
  WAREHOUSE_ACCEPTED: 'var(--color-primary-300)',
  ACCOUNTING_PENDING: 'var(--color-warn)',
  ACCOUNTING_APPROVED: 'var(--color-primary)',
  READY_FOR_PICKUP: 'var(--color-fill-mid)',
  HANDED_OVER: 'var(--color-fill-low)',
  RECEIPT_CONFIRMED: 'var(--color-success)',
  CLOSED: 'var(--color-primary-700)',
  REJECTED: 'var(--color-danger)',
  CANCELLED: 'var(--color-border-strong)',
};

export interface RequisitionStatusBlockProps {
  query: UseQueryResult<RequisitionReport, ApiClientError>;
}

export const RequisitionStatusBlock = ({ query }: RequisitionStatusBlockProps) => {
  const slices = useMemo<SliceDatum[]>(
    () =>
      (query.data?.byStatus ?? []).map((row) => ({
        key: row.status,
        label: requisitionStatusText(row.status),
        value: row.count,
        color: STATUS_COLOR[row.status],
      })),
    [query.data],
  );

  const avg = query.data?.avgCycleDays ?? null;

  return (
    <ReportCard
      icon={ClipboardList}
      tone="info"
      title={t('report.statusTitle')}
      hint={
        /* O'rtacha vaqt sarlavha ostida, halqa ichida emas: halqaning
           o'rtasida JAMI turadi va u yerga ikkinchi raqam qo'yilsa,
           ikkalasi ham o'qilmay qolardi. */
        avg === null ? t('report.statusNoCycle') : t('report.statusCycle', { days: avg })
      }
      pending={query.isPending}
      error={query.error ?? null}
      empty={slices.length === 0}
      emptyText={t('report.statusEmpty')}
    >
      <StatusDonut
        slices={slices}
        totalLabel={t('report.statusTotal')}
        labelOf={(key) => requisitionStatusText(key as RequisitionStatus)}
      />
    </ReportCard>
  );
};
